<template>
  <template v-if="debug">
    <!-- Only displayed for debugging the cross tab idle process detection  -->
    <div class="grid grid-cols-3 pl-8 text-yapgray-400">
      <code>REFRESH_MODAL_EXPIRATION: {{ REFRESH_MODAL_EXPIRATION / 1000 }}s</code>
      <code>INACTIVITY_TIMER_TIME: {{ INACTIVITY_TIMER_TIME / 1000 }}s</code>
      <code>IDLE_TABS_SYNC_TIMER: {{ IDLE_TABS_SYNC_TIMER / 1000 }}s</code>
    </div>
    <table class="w-full table-auto border-collapse">
      <thead>
        <tr>
          <th
            class="border-b p-4 pb-3 pl-8 pt-0 text-left font-medium text-yapgray-400 dark:border-yapdarkgray-600 dark:text-yapgray-200"
          >
            Tab #
          </th>
          <th
            class="border-b p-4 pb-3 pl-8 pt-0 text-left font-medium text-yapgray-400 dark:border-yapdarkgray-600 dark:text-yapgray-200"
          >
            time
          </th>
          <th
            class="border-b p-4 pb-3 pl-8 pt-0 text-left font-medium text-yapgray-400 dark:border-yapdarkgray-600 dark:text-yapgray-200"
          >
            lastActive
          </th>
          <th
            class="border-b p-4 pb-3 pl-8 pt-0 text-left font-medium text-yapgray-400 dark:border-yapdarkgray-600 dark:text-yapgray-200"
          >
            IdleFor
          </th>
        </tr>
      </thead>
      <tbody class="bg-white dark:bg-yapdarkgray-800">
        <tr v-for="data in Object.values(state.tabsIdleData)" :key="data.uuid">
          <td
            class="border-b border-yapdarkgray-100 p-4 pl-8 text-yapgray-500 dark:border-yapdarkgray-700 dark:text-yapgray-200"
          >
            {{ data.uuid }}
            <YapBadge
              value="self"
              class="bg-yapgray-300 text-yapgray-700 dark:bg-yapdarkgray-600 dark:text-primary-dark-100"
              v-if="data.uuid === state.uniqueTabId"
            />
          </td>
          <td
            class="border-b border-yapdarkgray-100 p-4 pl-8 text-yapgray-500 dark:border-yapdarkgray-700 dark:text-yapgray-200"
          >
            {{ data.time }}
          </td>
          <td
            class="border-b border-yapdarkgray-100 p-4 pl-8 text-yapgray-500 dark:border-yapdarkgray-700 dark:text-yapgray-200"
          >
            {{ data.lastActive }}
          </td>
          <td
            class="border-b border-yapdarkgray-100 p-4 pl-8 text-right font-mono text-yapgray-500 dark:border-yapdarkgray-700 dark:text-yapgray-200"
            :class="{ 'bg-primary-100': data.uuid === state.leastIdleData?.uuid }"
          >
            {{ data.idleFor }} sec
          </td>
        </tr>
      </tbody>
    </table>
  </template>
  <YapModal v-if="state.isInactive" :set-is-open="state.isModalOpen" @modal-closed="stayActive">
    <template #title>{{ $t('webapp.token.idle.title') }}</template>
    <template #default>
      <p>{{ $t('webapp.token.idle.txt') }}</p>
      <div class="my-4 flex items-center justify-center">
        <YapCountdownTimer :timer="REFRESH_MODAL_EXPIRATION / 1000" class="h-[100px] w-[100px]" smooth />
      </div>
    </template>
    <template #footer>
      <YapButton class="w-fit" outlined :button-size="ButtonSize.Medium" @click="logout">
        {{ $t('webapp.token.idle.button.logout') }}
      </YapButton>
      <YapButton class="w-fit" :loading="state.isLoading" :button-size="ButtonSize.Medium" @click="stayActive">
        {{ $t('webapp.token.idle.button.stay') }}
      </YapButton>
    </template>
  </YapModal>
</template>

<script setup lang="ts">
import { useIdle } from '@vueuse/core';
import { computed, reactive, watch, onMounted, onBeforeUnmount } from 'vue';

import getEnv from '@/utils/env';
import router from '@/router/router';
import { RouteNames } from '@/router/routes';
import YapModal from '@/components/YapModal.vue';
import YapBadge from '@/components/YapBadge.vue';
import YapButton, { ButtonSize } from '@/components/YapButton.vue';
import YapCountdownTimer from '@/components/YapCountdownTimer.vue';
import { logoutAndResetAllStoresWithCid } from '@/store/storeUtils';
import { BroadcastMessageTypes, IDLE_TABS_SYNC_BROADCAST_CHANNEL } from '@/utils/general';

const REFRESH_MODAL_EXPIRATION = 60_000; // 60sec
const INACTIVITY_TIMER_TIME = ['local', 'sandbox'].includes(getEnv('VITE_APP_ENV')) ? 60 * 60 * 1000 : 5 * 60 * 1000; // 60min in local or sandbox 5min elsewhere
const TAB_ID = 'TAB_ID';
const IDLE_TABS_SYNC_TIMER = 5_000;

const { lastActive } = useIdle(INACTIVITY_TIMER_TIME, {
  events: ['mousedown', 'keydown', 'touchstart', 'wheel'],
  listenForVisibilityChange: false
});

const props = defineProps<{
  debug?: boolean;
}>();

interface TabsIdleData {
  type: BroadcastMessageTypes;
  uuid: string;
  time: number;
  lastActive: number;
}
interface State {
  inactivityModalTimer?: number;
  idleTabSyncTimer?: number;
  idleTabSyncCleanerTimer?: number;
  isInactive: boolean;
  isModalOpen: boolean;
  isLoading: boolean;
  uniqueTabId?: string;
  tabsIdleData: { [key: string]: TabsIdleData & { idleFor: number } };
  leastIdleData?: TabsIdleData & { idleFor: number };
}
const state: State = reactive({
  inactivityModalTimer: undefined,
  idleTabSyncTimer: undefined,
  idleTabSyncCleanerTimer: undefined,
  isInactive: false,
  isModalOpen: false,
  isLoading: false,
  uniqueTabId: sessionStorage.getItem(TAB_ID) ?? undefined,
  tabsIdleData: {},
  leastIdleData: computed(() => [...Object.values(state.tabsIdleData)].sort((a, b) => a.idleFor - b.idleFor)[0])
});

onMounted(() => {
  initIdleTabSync();
});

onBeforeUnmount(() => {
  clearInterval(state.inactivityModalTimer);
  state.inactivityModalTimer = undefined;
  clearInterval(state.idleTabSyncTimer);
  state.idleTabSyncTimer = undefined;
  clearInterval(state.idleTabSyncCleanerTimer);
  state.idleTabSyncCleanerTimer = undefined;
  bc.close();
});

watch(
  () => state.leastIdleData?.idleFor,
  (newVal) => {
    if (!state.isModalOpen && newVal && newVal * 1000 >= INACTIVITY_TIMER_TIME) {
      inactivityDetected();
    }
  }
);

function inactivityDetected() {
  state.isModalOpen = true;
  state.isInactive = true;
  state.inactivityModalTimer = window.setInterval(() => logout(), REFRESH_MODAL_EXPIRATION);
}

const bc = new BroadcastChannel(IDLE_TABS_SYNC_BROADCAST_CHANNEL);
function initIdleTabSync() {
  if (!state.uniqueTabId) {
    const id = crypto.randomUUID();
    sessionStorage.setItem(TAB_ID, id);
    state.uniqueTabId = id;
  }
  bc.onmessage = (event) => {
    const data: TabsIdleData = event.data;
    if (data.type === BroadcastMessageTypes.IdleData) {
      const now = new Date().getTime();
      const idleTime = Math.floor((now - data.lastActive) / 1000);
      state.tabsIdleData[data.uuid] = { ...data, ...{ idleFor: idleTime } };
      if (idleTime < INACTIVITY_TIMER_TIME / 1000 && state.isModalOpen) {
        stayActive();
      }
    } else if (data.type === BroadcastMessageTypes.Logout) {
      logout();
    }
  };
  if (!state.idleTabSyncTimer) {
    state.idleTabSyncTimer = window.setInterval(() => {
      const now = new Date().getTime();
      const data: TabsIdleData = {
        type: BroadcastMessageTypes.IdleData,
        uuid: state.uniqueTabId ?? 'undefined',
        time: now,
        lastActive: lastActive.value
      };
      if (!state.isModalOpen) {
        // Only update broadcast idleState if we are not showing the countdown
        bc.postMessage(data);
        const idleFor = Math.floor((now - lastActive.value) / 1000);
        state.tabsIdleData[data.uuid] = { ...data, ...{ idleFor: idleFor } };
      }
    }, IDLE_TABS_SYNC_TIMER);
    state.idleTabSyncCleanerTimer = window.setInterval(
      () => {
        const now = new Date().getTime();
        const closedTabs = Object.values(state.tabsIdleData).filter(
          (data) => data.time < now - IDLE_TABS_SYNC_TIMER * 2
        );
        closedTabs.forEach((closedTab) => delete state.tabsIdleData[closedTab.uuid]);
      },
      Math.max(IDLE_TABS_SYNC_TIMER * 2, 5_000)
    ); // double IDLE_TABS_SYNC_TIMER but at least 5sec
  }
}

function logout() {
  logoutAndResetAllStoresWithCid();
  router.replace({ name: RouteNames.Login });
}

function stayActive() {
  clearInterval(state.inactivityModalTimer);
  state.inactivityModalTimer = undefined;
  state.isModalOpen = false;
  state.isInactive = false;
}
</script>
